import {
  and,
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
  WriteBatch,
  deleteField,
  writeBatch,
} from "firebase/firestore";
import { AnalysisDocument } from "~/src/type/firebase/firestore";
import { AnalysisArticle, GraphInfo, HeadingAnalysisArticle } from "~/src/type/ui/article/article";
import { deleteAnalysisArticleImages } from "~/src/lib/firebase/storage";
import { firestore } from "../firebase/config";
import {
  parseFirestoreToAnalysisDocument,
  parseFirestoreToAnalysisDocumentList,
  parseFirestoreToGraphDocument,
  sortGraphInfoListByIndex,
} from "./common";
import { removeDraft } from "./draft";
import {
  COLLECTION_ANALYSIS,
  COLLECTION_GRAPH,
  FIELD_CREATE_DATE,
  FIELD_PERMISSION_ADMIN,
  FIELD_PERMISSION_USER,
  FIELD_USER_ID,
} from "./info";

//------------
// 分析記事ヘッダ取得結果
//------------
export type ReadPartQueryResult = {
  readLast: QueryDocumentSnapshot<DocumentData, DocumentData> | undefined;
  list: HeadingAnalysisArticle[];
};

/**
 * 分析記事ドキュメントID発行
 * @note 発行元特定のため、firestoreのドキュメントIDは必ずこの関数から発行すること！
 */
export function createNewAnalysisDocumentId(): string {
  //------------
  // 自動ID発行
  //------------
  const topCollection = collection(firestore, COLLECTION_ANALYSIS);
  return doc(topCollection).id;
}

/**
 * firestore 自動発行IDフォーマット検証
 */
export function isFirestoreAutoId(id: string): boolean {
  //------------
  // 自動発行IDの検証
  //------------
  // ・20文字
  // ・英大小 + 数字
  const firestoreIdPattern = /^[A-Za-z0-9]{20}$/;

  // 検証
  return firestoreIdPattern.test(id);
}

/**
 * 分析記事リスト取得
 * @contents 指定されたクエリを実行して、分析記事リストを取得する
 */
export async function readAnalysisList(
  query: Query<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  console.log("readAnalysisList 開始");

  //------------
  // 取得
  //------------
  // クエリ発行して、分析記事を取得
  const querySnapshot = await getDocs(query);
  if (querySnapshot.empty) {
    // 1件もなければ、なし
    return {
      readLast: undefined,
      list: [],
    };
  }

  console.log("readAnalysisList 記事の取得数=" + querySnapshot.size);

  //------------
  // ドキュメントをUI向けデータ構造にパース
  //------------
  // 分析記事リスト
  const analysisList: HeadingAnalysisArticle[] = await parseFirestoreToAnalysisDocumentList(
    querySnapshot
  );

  return {
    readLast: querySnapshot.docs[querySnapshot.docs.length - 1],
    list: analysisList,
  };
}

/**
 * 最新の分析記事リスト取得
 *
 * @param 取得数
 * @param 取得する1つ前の取得分析記事（指定された場合、この次の記事から取得する）
 */
export async function readLatestAnalysisList(
  readSize: number,
  justBeforeArticle?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  console.log("readLatestAnalysisList 開始");

  //------------
  // クエリ
  // @note 最新とする対象フィールドは「作成日時：createdAt」とする。
  //       「更新日時：updatedAt」は考慮しない。
  //------------
  let q = query(
    collection(firestore, COLLECTION_ANALYSIS),
    where(FIELD_PERMISSION_USER, "==", true),
    where(FIELD_PERMISSION_ADMIN, "==", true),
    orderBy(FIELD_CREATE_DATE, "desc"),
    limit(readSize)
  );

  // 直前の読み込み記事があれば、その次から取得
  if (justBeforeArticle) {
    q = query(q, startAfter(justBeforeArticle));
  }

  //------------
  // 記事読み込み
  //------------
  return await readAnalysisList(q);
}

/**
 * 特定ユーザーの投稿分析記事リスト取得
 *
 * @param 取得数
 * @param 取得対象uid
 * @param 取得する1つ前の取得分析記事（指定された場合、この次の記事から取得する）
 */
export async function readUserAnalysisList(
  readSize: number,
  userid: string,
  justBeforeArticle?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  //------------
  // クエリ
  //------------
  let q = query(
    collection(firestore, COLLECTION_ANALYSIS),
    where(FIELD_USER_ID, "==", userid),
    orderBy(FIELD_CREATE_DATE, "desc"),
    limit(readSize)
  );

  // 直前の読み込み記事があれば、その次から取得
  if (justBeforeArticle) {
    q = query(q, startAfter(justBeforeArticle));
  }

  //------------
  // 記事読み込み
  //------------
  return await readAnalysisList(q);
}

/**
 * 分析記事検索
 *
 * @param 取得数
 * @param 検索ワード
 * @param 取得する1つ前の取得分析記事（指定された場合、この次の記事から取得する）
 */
export async function readSearchAnalysisList(
  readSize: number,
  keyword: string,
  justBeforeArticle?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<ReadPartQueryResult> {
  //------------
  // クエリ
  //------------
  let q = query(
    collection(firestore, COLLECTION_ANALYSIS),
    and(
      where(FIELD_PERMISSION_USER, "==", true),
      where(FIELD_PERMISSION_ADMIN, "==", true),
      or(where("tag", "array-contains", keyword), where("works", "array-contains", keyword))
    ),
    orderBy(FIELD_CREATE_DATE, "desc"),
    limit(readSize)
  );

  // 直前の読み込み記事があれば、その次から取得
  if (justBeforeArticle) {
    q = query(q, startAfter(justBeforeArticle));
  }

  //------------
  // 記事読み込み
  //------------
  return await readAnalysisList(q);
}

/**
 * 分析記事取得（グラフデータ含む）
 */
export async function getArticleWithGraph(id: string): Promise<AnalysisArticle | undefined> {
  //------------
  // 分析記事の取得
  //------------
  const docRef = doc(firestore, COLLECTION_ANALYSIS, id);
  const docSnap = await getDoc(docRef);

  // 分析記事のデータ構造にパース
  const analysisContents = parseFirestoreToAnalysisDocument(docSnap);

  //------------
  // 分析記事内のグラフ
  //------------
  const coll = collection(docRef, COLLECTION_GRAPH);
  const graphSnapshot = await getDocs(coll);

  // console.log("getArticleWithGraph() graphSnapshot.size=" + graphSnapshot.size);

  // 複数のグラフドキュメントを1件ずつパースする
  const graphInfoList: GraphInfo[] = graphSnapshot.docs.map((graphSnap) => {
    const id = graphSnap.id;
    const info = parseFirestoreToGraphDocument(graphSnap);

    // console.log("getArticleWithGraph() graphSnap.id=" + graphSnap.id);
    // console.log("getArticleWithGraph() info.kind=" + info.kind);

    return {
      id: id,
      info: info,
    };
  });

  // indexでソート（indexがundefinedのものは末尾に）
  const sortedGraphInfoList = sortGraphInfoListByIndex(graphInfoList);

  return {
    id: id,
    contents: analysisContents,
    graph: sortedGraphInfoList,
  };
}

/**
 * 分析記事ドキュメントの新規追加／更新
 *   @content 指定された記事の投稿状態に応じて、新規追加か更新処理を行う
 */
export async function postArticleDocument(
  userid: string,
  userName: string,
  id: string,
  analysisDocument: AnalysisDocument,
  graphInfoList: GraphInfo[]
): Promise<boolean> {
  try {
    //------------
    // 新規投稿判定
    //------------
    // 指定された記事が投稿済みかどうか
    const isPosted = await isPostArticle(id);
    if (isPosted === undefined) {
      // 処理エラーの場合、失敗とする
      return false;
    }

    //------------
    // 新規投稿
    //------------
    if (!isPosted) {
      await createArticleDocument(userid, userName, id, analysisDocument, graphInfoList);
      return true;
    }

    //------------
    // 投稿済み記事の更新
    //------------
    await updateArticleDocument(userid, userName, id, analysisDocument, graphInfoList);
    return true;
  } catch (e) {
    console.error("Error postArticleDocument() : ", e);
  }

  return false;
}

/**
 * 分析記事ドキュメントの新規作成
 */
export async function createArticleDocument(
  userid: string,
  userName: string,
  id: string,
  analysisDocument: AnalysisDocument,
  graphInfoList: GraphInfo[]
) {
  console.log("createArticleDocument 開始 analysisDocument=", analysisDocument);

  const batch = writeBatch(firestore);

  // 参照
  const topCollection = collection(firestore, COLLECTION_ANALYSIS);
  const docRef = doc(topCollection, id);

  //------------
  // 分析記事データ設定
  //------------
  // ユーザー名（記事作成中に変更されるケースもあるため、投稿直前に設定する）
  analysisDocument.userName = userName;

  //------------
  // 分析記事データ設定（管理系の情報）
  //------------
  // 作成日時の設定
  analysisDocument.createdAt = serverTimestamp();

  // 下書き編集日時の設定（下書き保存を経由せず直接投稿した場合に備える）
  analysisDocument.draftUpdatedAt = serverTimestamp();

  // 公開許可
  analysisDocument.permissionUser = true; // ユーザー公開許可
  analysisDocument.permissionAdmin = true; // 管理者公開許可

  //------------
  // 保存
  //------------
  // 分析記事
  console.log("createArticleDocument setDoc() analysisDocument=", analysisDocument);
  batch.set(docRef, analysisDocument);

  // グラフ
  const subGraphCollection = collection(docRef, COLLECTION_GRAPH);
  for (const graphInfo of graphInfoList) {
    const newDocRef = doc(subGraphCollection, graphInfo.id);
    batch.set(newDocRef, graphInfo.info);
  }

  //------------
  // 下書きの削除
  //------------
  await removeDraft(userid, id, batch);

  // コミット
  await batch.commit();
}

/**
 * 分析記事ドキュメントの更新
 */
export async function updateArticleDocument(
  userid: string,
  userName: string,
  id: string,
  analysisDocument: AnalysisDocument,
  graphInfoList: GraphInfo[]
) {
  console.log("updateArticleDocument 開始");

  const batch = writeBatch(firestore);

  // 参照
  const topCollection = collection(firestore, COLLECTION_ANALYSIS);
  const docRef = doc(topCollection, id);

  //------------
  // 分析記事データ設定
  //------------
  // ユーザー名（記事作成中に変更されるケースもあるため、投稿直前に設定する）
  analysisDocument.userName = userName;

  //------------
  // 分析記事データ設定（管理系の情報）
  //------------
  // 更新日時の設定
  analysisDocument.updatedAt = serverTimestamp();

  // 公開許可（ユーザー公開許可のみ公開設定）
  analysisDocument.permissionUser = true; // ユーザー公開許可

  //------------
  // 保存
  //------------
  if ("body" in analysisDocument && analysisDocument.body) {
    // 新形式：旧テキストフィールドをFirestoreから削除
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: Record<string, any> = {
      ...analysisDocument,
      details: deleteField(),
      premise: deleteField(),
      graphDescription: deleteField(),
      conclusion: deleteField(),
      reference: deleteField(),
    };
    batch.update(docRef, updateData);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    batch.update(docRef, analysisDocument as any);
  }

  //============
  // グラフ
  //============
  // Firestore上の既存グラフを取得
  const subGraphCollection = collection(docRef, COLLECTION_GRAPH);
  const savedGraphList = await getDocs(subGraphCollection);

  //------------
  // グラフの更新・削除
  //------------
  // 更新完了リスト
  const updatedGraph: string[] = [];

  // まずは、DBに対して更新・削除から行う
  for (const docSnapshot of savedGraphList.docs) {
    // DB上のグラフが、更新対象にあるかどうか
    const existGraph = graphInfoList.find((info) => info.id === docSnapshot.id);
    if (existGraph) {
      //------------
      // 更新
      //------------
      const graphRef = doc(subGraphCollection, docSnapshot.id);
      batch.update(graphRef, existGraph.info);

      //------------
      // 削除対象外
      //------------
      // 更新完了リストに追加
      updatedGraph.push(existGraph.id);

      continue;
    }

    //------------
    // 削除
    //------------
    // ない場合、firestoreから削除
    const graphRef = doc(subGraphCollection, docSnapshot.id);
    batch.delete(graphRef);
  }

  //------------
  // グラフの新規追加
  //------------
  for (const graphInfo of graphInfoList) {
    const exist = updatedGraph.find((id) => id === graphInfo.id);

    // 更新済みなら処理なし
    if (exist) {
      continue;
    }

    // 新ドキュメントとして登録
    const graphRef = doc(subGraphCollection, graphInfo.id);
    batch.set(graphRef, graphInfo.info);
  }

  //------------
  // 下書きの削除
  //------------
  await removeDraft(userid, id);

  // コミット
  await batch.commit();
}

/**
 * 投稿済み分析記事判定
 */
export async function isPostArticle(id: string): Promise<boolean | undefined> {
  //------------
  // 参照を取得
  //------------
  // 分析記事コレクション
  const topCollection = collection(firestore, COLLECTION_ANALYSIS);
  const docRef = doc(topCollection, id);

  try {
    //------------
    // 実体を取得
    //------------
    const docData = await getDoc(docRef);

    // 存在の有無を返す
    return docData.exists();
  } catch (e) {
    console.log("Error isPostArticle() : ", e);

    //------------
    // エラー
    //------------
    // 不明とする
    return undefined;
  }
}

/**
 * 分析記事ドキュメントのユーザー名更新
 */
export async function updateArticleUserName(batch: WriteBatch, userId: string, userName: string) {
  try {
    //------------
    // クエリの生成
    //------------
    const q = query(collection(firestore, COLLECTION_ANALYSIS), where("userId", "==", userId));

    //------------
    // 一括書き込み
    //------------
    const analysisDocs = await getDocs(q);
    analysisDocs.docs.forEach((item) => {
      batch.update(item.ref, { userName: userName });
    });
  } catch (e) {
    console.error("Error updateArticleUserName() : ", e);
  }
}

/**
 * 分析記事ドキュメントの公開許可の変更
 */
export async function updateArticleUserPermission(docId: string, current: boolean) {
  //------------
  // 更新
  //------------
  const docRef = doc(collection(firestore, COLLECTION_ANALYSIS), docId);
  await updateDoc(docRef, { permissionUser: !current });
}

/**
 * 投稿済み分析記事の削除
 *   バッチ処理で以下のデータを削除
 *   ・分析記事
 *   ・分析記事のグラフ
 *   ・下書き記事（削除対象の記事の下書きデータ）
 */
export async function removePostedArticle(userId: string, articleId: string) {
  //------------
  // バッチ
  //------------
  const batch = writeBatch(firestore);

  //------------
  // グラフ削除
  //------------
  // 投稿記事
  const articleDocRef = doc(firestore, COLLECTION_ANALYSIS, articleId);

  // グラフ
  const graphCollection = collection(articleDocRef, COLLECTION_GRAPH);
  const graphsSnap = await getDocs(graphCollection);

  // 全て削除
  for (const doc of graphsSnap.docs) {
    batch.delete(doc.ref);
  }

  //------------
  // 投稿記事の削除
  //------------
  batch.delete(articleDocRef);

  //------------
  // 下書き記事（投稿済みで編集中の下書き記事を対象とする）
  //------------
  await removeDraft(userId, articleId, batch);

  //------------
  // コミット
  //------------
  await batch.commit();

  //------------
  // 本文画像のクリーンアップ
  //------------
  // 投稿記事・下書きとも削除済みのため、どこからも参照されなくなった画像を削除する
  await deleteAnalysisArticleImages(userId, articleId).catch((error) => {
    console.error("[removePostedArticle] 画像クリーンアップエラー", error);
  });
}
